/**
 * Entry Checklist Types
 *
 * This file provides types for entry UI components. It re-exports generated OpenAPI types
 * and provides UI-friendly types where the API uses strings for numeric values.
 *
 * Note: The API returns decimal values as strings (Pydantic Decimal serialization).
 * UI types use numbers for easier arithmetic in components.
 */

// Re-export generated API response types for direct API usage
export type {
  WaterReadingResponse,
  WaterReadingCreate,
  WaterReadingUpdate,
  FeedingResponse,
  FeedingCreate,
  FeedingUpdate,
  SamplingResponse,
  SamplingCreate,
  SamplingUpdate,
  ApplicationResponse,
  ApplicationCreate,
  ApplicationEditRequest as ApplicationUpdate,
  HarvestResponse,
  HarvestCreate,
  HarvestUpdate,
  FeedRecommendationResponse,
  ChecklistResponse as GeneratedChecklistResponse,
  PondEntryStatus as GeneratedChecklistPond,
  CompletionStatus as GeneratedCompletionStatus,
} from './generated/openapi';

// Convenience type aliases (these match the generated enum/union types)
// Authoritative monitoring model: 5am, 7am, 3pm, 7pm, 10pm.
export type ReadingSlot = '5am' | '7am' | '3pm' | '7pm' | '10pm';
export type CompletionStatus = 'complete' | 'partial' | 'pending';
export type SamplingStatus = 'complete' | 'pending' | 'not_due';
export type ApplicationCategory =
  | 'probiotic'
  | 'water_treatment'
  | 'chemical'
  | 'feed_additive'
  | 'antibiotic'
  | 'supplement'
  | 'other';

/**
 * UI-friendly Feeding type with numeric fields.
 */
export interface FeedBlendItem {
  feed_type: string;
  percent: number | string;
}

export interface Feeding {
  id: string;
  cycle_id: string;
  farm_id: string;
  feeding_date: string;
  feeding_slot: string;
  feed_amount_kg: number;
  feed_type: string | null;
  feed_brand: string | null;
  feed_tray_check: string | null;
  /** Optional mix (#361): feed_amount_kg is the total; null = a single product. */
  blend?: FeedBlendItem[] | null;
  blend_amounts_kg?: Array<string | number> | null;
  notes: string | null;
  data_source: string;
  technician_id: string | null;
  created_at: string;
  last_modified: string;
}

/**
 * UI-friendly Feed Recommendation with numeric fields.
 * Matches backend/app/schemas/feed_calculator.py:FeedRecommendationResponse
 */
export interface FeedRecommendation {
  daily_feed_kg: number;
  feed_type: string;
  calculation_method: 'doc_based' | 'abw_based';
  doc: number;
  stocking_count: number;
  abw_g?: number | null;
  pieces_alive?: number | null;
  biomass_kg?: number | null;
  feed_pct?: number | null;
  tray_g_per_kg?: number | null;
  check_time_hours?: number | null;
  feedings_per_day: number;
  feeding_times: string[];
  warning?: string | null;
}

/**
 * UI-friendly Sampling type with numeric fields.
 */
export interface Sampling {
  id: string;
  farm_id?: string;
  cycle_id: string;
  sampling_date: string;
  sample_count: number;
  total_weight_g: number;
  mortality_count: number | null;
  notes: string | null;
  data_source?: string;
  technician_id?: string | null;
  abw_g?: number | null;
  estimated_pieces_alive?: number | null;
  survival_rate_pct?: number | null;
  estimated_biomass_kg?: number | null;
  created_at?: string;
  last_modified?: string;
  deleted_at?: string | null;
}

/**
 * UI-friendly Application type with numeric fields.
 */
export interface Application {
  id: string;
  farm_id?: string;
  cycle_id: string;
  application_date: string;
  product_id?: string | null;
  product_name: string;
  product_category: ApplicationCategory;
  amount: number;
  unit: string;
  reason?: string | null;
  notes: string | null;
  technician_id?: string | null;
  created_at?: string;
  last_modified?: string;
  deleted_at?: string | null;
}

/**
 * UI-friendly Harvest type with numeric fields.
 */
export interface Harvest {
  id: string;
  farm_id?: string;
  cycle_id: string;
  harvest_date: string;
  total_weight_kg: number;
  piece_count: number | null;
  avg_weight_g?: number | null;
  buyer?: string | null;
  size_grade: string | null;
  price_per_kg: number | null;
  harvest_type: 'partial' | 'final';
  notes: string | null;
  total_revenue?: number | null;
  created_at?: string;
  last_modified?: string;
  deleted_at?: string | null;
}

/**
 * Normalized ChecklistPond with camelCase fields for UI components.
 * Transforms snake_case API response to camelCase for consistent frontend usage.
 */
export interface ChecklistPond {
  id: string;
  name: string;
  isAssigned: boolean;
  activeCycle?: {
    id: string;
    doc: number;
  };
  completion: {
    water: CompletionStatus;
    feeding: CompletionStatus;
    sampling: SamplingStatus;
  };
  expectedSlots: string[];
  /** Slots whose field tests are all filled (server rule; lab tests optional). */
  completedSlots: string[];
  /** Slots with any field test filled: the completed ones plus the partial ones. */
  startedSlots: string[];
  /** Only with `include_preparing=true` (phone Today). */
  preparingCycle?: {
    id: string;
    prepDay: number | null;
    plannedStockingDate: string | null;
  };
  applicationsCount: number;
}

/**
 * Normalized ChecklistResponse with camelCase ponds.
 */
export interface ChecklistResponse {
  date: string;
  ponds: ChecklistPond[];
  total_assigned: number;
  completed_count: number;
}

// Import types needed for normalization functions
import type {
  ChecklistResponse as ApiChecklistResponse,
  PondEntryStatus,
} from './generated/openapi';

/**
 * Normalizes a PondEntryStatus from the API to a ChecklistPond with camelCase fields.
 */
export function normalizeChecklistPond(pond: PondEntryStatus): ChecklistPond {
  return {
    id: pond.id,
    name: pond.name,
    isAssigned: pond.is_assigned,
    activeCycle: pond.active_cycle
      ? {
          id: pond.active_cycle.id,
          doc: pond.active_cycle.doc,
        }
      : undefined,
    completion: {
      water: pond.completion.water as CompletionStatus,
      feeding: pond.completion.feeding as CompletionStatus,
      sampling: pond.completion.sampling as SamplingStatus,
    },
    expectedSlots: pond.expected_slots,
    completedSlots: pond.completed_slots,
    startedSlots: pond.started_slots ?? [],
    preparingCycle: pond.preparing_cycle
      ? {
          id: pond.preparing_cycle.id,
          prepDay: pond.preparing_cycle.prep_day ?? null,
          plannedStockingDate: pond.preparing_cycle.planned_stocking_date ?? null,
        }
      : undefined,
    applicationsCount: pond.applications_count ?? 0,
  };
}

/**
 * Normalizes a ChecklistResponse from the API to use camelCase ponds.
 */
export function normalizeChecklistResponse(
  response: ApiChecklistResponse
): ChecklistResponse {
  return {
    date: response.date,
    ponds: response.ponds.map(normalizeChecklistPond),
    total_assigned: response.total_assigned,
    completed_count: response.completed_count,
  };
}
